// src/pages/StudentCertificates.js
import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchCertificates, downloadCertificate } from '../../../services/studentService';
import './StudentCertificates.css';

function StudentCertificates() {
  const { user } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchCertificates();
      setCertificates(data);
    } catch (error) {
      setError('Error loading certificates: ' + (error.response?.data?.msg || error.message));
      console.error('Sertifikalar yüklenirken hata oluştu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (courseId) => {
    try {
      setDownloadingId(courseId);
      await downloadCertificate(courseId);
    } catch (error) {
      setError('Error downloading certificate: ' + (error.response?.data?.msg || error.message));
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="student-certificates-loading">
        <div className="loading-spinner">🏆</div>
        <p>Loading certificates...</p>
      </div>
    );
  }

  return (
    <div className="student-certificates">
      {/* Header */}
      <div className="certificates-header">
        <div className="header-content">
          <h1>🏆 My Certificates</h1>
          <p>Certificates from courses you have completed</p>
        </div>
        <button onClick={loadCertificates} className="refresh-btn" disabled={loading}>
          <span className="refresh-icon">🔄</span>
          Refresh
        </button>
      </div>

      {error && (
        <div className="certificates-error-banner">
          <span>⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}

      {/* Certificate List */}
      {certificates.length === 0 ? (
        <div className="empty-certificates">
          <div className="empty-icon">🏆</div>
          <h3>You don't have any certificates yet</h3>
          <p>Start completing courses to earn certificates</p>
          
          <div className="certificate-info">
            <h4>📋 Certificate Requirements:</h4>
            <ul>
              <li>✅ Complete at least 80% of the course</li>
              <li>✅ Attend at least 75% of the lessons</li>
              <li>✅ Pass the final exam (if applicable)</li>
              <li>✅ Submit assignments and projects on time</li>
            </ul>
          </div>

          <div className="featured-courses">
            <h4>🎯 Courses You Can Complete</h4>
            <p>Complete your courses to earn your first certificate</p>
          </div>
        </div>
      ) : (
        <div className="certificates-grid">
          {certificates.map(cert => (
            <div key={cert._id} className="certificate-card">
              <div className="certificate-icon">🏆</div>
              <div className="certificate-content">
                <h3>{cert.courseTitle}</h3>
                <p className="certificate-date">Completed: {formatDate(cert.completedAt)}</p>
                <p className="certificate-grade">Grade: {cert.grade || 'Pass'}</p>
                <button 
                  className="download-btn"
                  onClick={() => handleDownload(cert.courseId)}
                  disabled={downloadingId === cert.courseId}
                >
                  {downloadingId === cert.courseId ? (
                    <>⏳ Downloading...</>
                  ) : (
                    <>📥 Download Certificate</>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Coming Soon Features */}
      <div className="coming-soon">
        <div className="coming-soon-content">
          <h3>🚀 Coming Soon</h3>
          <div className="features-list">
            <div className="feature-item">
              <span className="feature-icon">📜</span>
              <div className="feature-text">
                <strong>Digital Certificates</strong>
                <p>Digital certificates for completed courses</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📊</span>
              <div className="feature-text">
                <strong>Progress Tracking</strong>
                <p>View your course progress in detail</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🏅</span>
              <div className="feature-text">
                <strong>Achievement Badges</strong>
                <p>Earn badges for your achievements</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentCertificates;